<template>
    <v-col v-if="wrapCol">
        <v-select
            v-if="type == 'select'"
            v-model="value"
            :error-messages="errorMessage"
            :color="color"
            :hint="hint"
            :item-title="itemTitle" :item-value="itemValue"
            :items="selectItems" :label="label" :readonly="readOnly"
            :prepend-icon="prependIcon">
        </v-select>
        <v-otp-input
            v-else-if="type == 'otp' && (typeof value == 'string' || value == undefined && value != null)"
            v-model="value"
            :error-messages="errorMessage"
            :autofocus="true"
            :color="color"
        ></v-otp-input>
        <v-switch v-else-if="type == 'switch' && typeof value === 'boolean'"
                  :error-messages="errorMessage"
                  v-model="value" :color="color"
                  :hint="hint" :label="label" hide-details="auto"
                  :readonly="readOnly"
                  :prepend-icon="prependIcon" />
        <v-text-field v-else-if="type =='password'" v-model="value"
                      :error-messages="errorMessage"
                      :hint="hint" :label="label" :placeholder="placeholder"
                      :readonly="readOnly" :type="passwordField"
                      :prepend-icon="prependIcon">
            <template #append-inner v-if="passwordIcon">
                <v-icon @click="handlePassword">
                    {{ passwordField == 'password' ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
                </v-icon>
            </template>
            <template #append-inner v-else-if="addedIcon">
                <v-icon>{{ addedIcon }}</v-icon>
            </template>

        </v-text-field>
        <v-number-input v-else-if="type =='number'" v-model="value"
                        :control-variant="controlVariant"
                        :error-messages="errorMessage"
                        :hint="hint" :label="label" :placeholder="placeholder"
                        :readonly="readOnly" :type="type"
                        :prepend-icon="prependIcon"
        >
            <template #append-inner v-if="addedIcon">
                <v-icon>{{ addedIcon }}</v-icon>
            </template>
        </v-number-input>
        <v-text-field v-else v-model="value"
                      :error-messages="errorMessage"
                      :hint="hint" :label="label" :placeholder="placeholder"
                      :readonly="readOnly" :type="type"
                      :prepend-icon="prependIcon">
            <template #append-inner v-if="addedIcon">
                <v-icon>{{ addedIcon }}</v-icon>
            </template>

        </v-text-field>
    </v-col>
    <template v-else>
        <v-select
            v-if="type == 'select'"
            v-model="value"
            :error-messages="errorMessage"
            :color="color"
            :hint="hint"
            :item-title="itemTitle" :item-value="itemValue"
            :items="selectItems" :label="label" :readonly="readOnly"
            :prepend-icon="prependIcon"
        ></v-select>
        <v-otp-input
            v-else-if="type == 'otp' && (typeof value == 'string' || value == undefined && value != null)"
            v-model="value"
            :error-messages="errorMessage"
            :autofocus="true"
            :color="color"
        ></v-otp-input>
        <v-switch v-else-if="type == 'switch' && typeof value === 'boolean'"
                  :error-messages="errorMessage"
                  v-model="value" :color="color"
                  :hint="hint" :label="label" hide-details="auto"
                  :readonly="readOnly" :prepend-icon="prependIcon"/>
        <v-text-field v-else-if="type =='password'" v-model="value"
                      :error-messages="errorMessage"
                      :hint="hint" :label="label" :placeholder="placeholder"
                      :readonly="readOnly" :type="passwordField"
                      :prepend-icon="prependIcon">
            <template #append-inner v-if="passwordIcon">
                <v-icon @click="handlePassword">
                    {{ passwordField == 'password' ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
                </v-icon>
            </template>
            <template #append-inner v-else-if="addedIcon">
                <v-icon>{{ addedIcon }}</v-icon>
            </template>

        </v-text-field>
        <v-number-input v-else-if="type =='number'" v-model="value"
                        :control-variant="controlVariant"
                        :error-messages="errorMessage"
                        :hint="hint" :label="label" :placeholder="placeholder"
                        :readonly="readOnly" :type="type"
                        :prepend-icon="prependIcon"
        >
            <template #append-inner v-if="addedIcon">
                <v-icon>{{ addedIcon }}</v-icon>
            </template>
        </v-number-input>
        <v-text-field v-else v-model="value"
                      :error-messages="errorMessage"
                      :hint="hint" :label="label" :placeholder="placeholder"
                      :readonly="readOnly" :type="type"
                      :prepend-icon="prependIcon">
            <template #append-inner v-if="addedIcon">
                <v-icon>{{ addedIcon }}</v-icon>
            </template>

        </v-text-field>
    </template>
</template>

<script lang="ts">
import {computed, defineComponent, type PropType, ref, type Ref, watch} from "vue";
import {watchDebounced} from "@vueuse/core";
import type {TColor} from "@/components/generics/types/TColor";
import type {TFieldTypes} from "@/components/generics/DynamicForm/types/TFieldTypes";
import type {TRule} from "@/components/generics/types/TRule";
import type {IWatch} from "@/components/generics/DynamicForm/interfaces/IWatch";
import {useFieldModel} from "@/components/generics/DynamicForm/composable/useFieldModel";
import {TRuleSet} from "@/components/generics/DynamicForm/composable/useRuleSet";

type Icon = () => string;

export default defineComponent({
    emits: ["update:modelValue", "update:name"],
    props: {
        modelValue: {
            type: [String, Number, Boolean],
        },
        wrapCol: {
            type: Boolean,
            default: true
        },
        name: {
            type: String,
        },
        label: {
            type: String,
        },
        hint: {
            type: String,
        },
        placeholder: {
            type: String,
        },
        type: {
            type: String as PropType<TFieldTypes>,
            default: 'text'
        },
        secretFieldKey: {
            type: String,
            default: "password"
        },
        hasIcon: {
            type: Boolean,
            default: false
        },
        tooltipText: {
            type: String,
            default: ""
        },
        itemTitle: {
            type: String,
            default: "title"
        },
        itemValue: {
            type: String,
            default: "value"
        },
        color: {
            type: String as PropType<TColor>,
        },
        selectItems: {
            type: Array
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        rules: {
            type: Object as PropType<TRule | TRuleSet>
        },
        passwordIcon: {
            type: Boolean,
            default: false
        },
        addedIcon: {
            type: String as PropType<Icon | string>
        },
        watch: {
            type: Object as PropType<IWatch>
        },
        secretFieldIsPwd: {
            type: Boolean,
            default: true
        },
        prependIcon: {
            type: String
        },
        controlVariant: {
            type: String as PropType<'split'|'stacked'|'default'>,
            default: 'default'
        }

    },
    setup(props, {emit}) {
        const {value, errorMessage} = useFieldModel(props, "modelValue", emit,
            {
                name: typeof props.name === "string" ? 'name' : undefined,
                nameIsProp: typeof props.name === "string", useVModel: true, rules: props.rules
            })
        const passwordField: Ref<'text' | 'password'> = ref('password')
        const handlePassword = () => {
            passwordField.value = passwordField.value == 'text' ? 'password' : 'text';
        }

        if (props.watch) {
            if (props.watch.hasOwnProperty('debounce')) {
                let debounce = props.watch.debounce;
                let maxWait = props.watch.maxWait;
                if (typeof debounce !== 'number') {
                    debounce = 1000;
                }
                if (typeof maxWait !== 'number') {
                    maxWait = 1000;
                }
                watchDebounced(
                    value,
                    props.watch.callback,
                    {debounce: debounce, maxWait: maxWait}
                )
            } else {
                watch(value, props.watch.callback)
            }
        }

        const addedIcon = computed(() => {
            if (props.addedIcon) {
                if (typeof props.addedIcon === 'function') {
                    return props.addedIcon()
                } else {
                    return props.addedIcon
                }
            }
        })
        return {
            value,
            errorMessage,
            addedIcon,
            passwordField,
            handlePassword,
        }
    },
})
</script>

